import { useState, useCallback, useEffect, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChatHistorySidebar } from './ChatHistorySidebar'

interface ChatLayoutProps {
  readonly children: ReactNode
}

export function ChatLayout({ children }: ChatLayoutProps) {
  const { t } = useTranslation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const navigate = useNavigate()

  const openSidebar = useCallback(() => setSidebarOpen(true), [])
  const closeSidebar = useCallback(() => setSidebarOpen(false), [])
  const goNewChat = useCallback(() => navigate('/chat'), [navigate])

  // Échap referme le panneau : le voile n'est cliquable qu'à la souris.
  useEffect(() => {
    if (!sidebarOpen) return

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setSidebarOpen(false)
    }

    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [sidebarOpen])

  return (
    <div className="flex h-screen overflow-hidden bg-surface">
      {/* Voile affiché derrière le panneau sur mobile */}
      {sidebarOpen && (
        <div
          data-chat-backdrop
          className="fixed inset-0 z-20 bg-black/40 lg:hidden"
          aria-hidden="true"
          onClick={closeSidebar}
        />
      )}

      {/* Left sidebar */}
      <ChatHistorySidebar
        open={sidebarOpen}
        onClose={closeSidebar}
        onNewChat={goNewChat}
      />

      {/* Main column */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="flex items-center gap-2 px-3 h-14 border-b border-outline-variant bg-surface/80 backdrop-blur-xl shrink-0">
          {/* Hamburger (visible on mobile, hides on desktop since sidebar is always visible) */}
          <button
            type="button"
            onClick={openSidebar}
            aria-label={t('chat.open_menu')}
            className="lg:hidden p-2 rounded-xl hover:bg-surface-low text-outline hover:text-on-surface transition-colors"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" aria-hidden="true">
              <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
            </svg>
          </button>

          {/* Logo / title — center on mobile */}
          <div className="flex-1 flex items-center justify-center lg:justify-start gap-2 min-w-0">
            <div className="lg:hidden w-6 h-6 rounded-full bg-primary flex items-center justify-center shrink-0">
              <span className="text-white text-[10px] font-bold leading-none">M</span>
            </div>
            <span className="text-sm font-bold text-primary font-display lg:hidden">{t('app.name')}</span>
          </div>

          {/* New chat button — top right (mobile only, to match ChatGPT pattern) */}
          <button
            type="button"
            onClick={goNewChat}
            aria-label={t('chat.new_conversation')}
            title={t('chat.new_conversation')}
            className="lg:hidden p-2 rounded-xl hover:bg-surface-low text-on-surface-variant hover:text-on-surface transition-colors"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
            </svg>
          </button>
        </header>

        {/* Page content */}
        <main className="flex-1 flex flex-col overflow-hidden">
          {children}
        </main>
      </div>
    </div>
  )
}
